import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { setUserProperties, trackEvent, generateEventId } from '@/lib/tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ResourceDownloadModal = ({ isOpen, onClose, resource }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    contactNumber: '',
    companyName: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/resources/download`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        contact_number: formData.contactNumber,
        company_name: formData.companyName,
        resource_name: resource.title,
        resource_type: resource.type
      });

      if (response.data.success) {
        // GA4 + GTM + Clarity (PII-stripped). Fires only on backend success.
        setUserProperties({
          company_name: formData.companyName,
          last_resource_downloaded: resource.title,
          source: 'resource_download',
        });
        trackEvent('resource_download', {
          resource_name: resource.title,
          resource_slug: resource.slug,
          event_id: generateEventId(),
          form_id: 'resource_download',
          resource_type: resource.type,
          has_company: Boolean(formData.companyName),
          has_phone: Boolean(formData.contactNumber),
        });

        toast.success("Your download is on its way — check your inbox.");
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          contactNumber: '',
          companyName: ''
        });
        onClose();
      }
    } catch (error) {
      console.error('Resource download error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to process download. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!resource) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-900 border-gray-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">
            Download Resource
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="bg-red-600/10 border border-red-600/20 rounded-lg p-4">
            <p className="text-sm text-gray-400 mb-1">Requesting:</p>
            <p className="font-semibold text-white">{resource.title}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-white">First Name *</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleChange('firstName', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                  placeholder="John"
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-white">Last Name *</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleChange('lastName', e.target.value)}
                  className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                  placeholder="Smith"
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                placeholder="john@company.com"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactNumber" className="text-white">Contact Number</Label>
              <Input
                id="contactNumber"
                type="tel"
                value={formData.contactNumber}
                onChange={(e) => handleChange('contactNumber', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                placeholder="+27 123 456 7890"
                disabled={isSubmitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-white">Company Name</Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                className="bg-gray-800 border-gray-700 text-white placeholder:text-gray-500"
                placeholder="Your Company"
                disabled={isSubmitting}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={onClose}
                className="flex-1 btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="flex-1 btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Get Download Link'}
              </Button>
            </div>
          </form>

          <p className="text-xs text-gray-500 text-center">
            We'll email you a download link instantly. Check your spam folder if you don't see it.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResourceDownloadModal;
